import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Popper,
  CircularProgress,
  TextField,
  IconButton,
  Fade,
  Tooltip,
  Button,
  Snackbar,
  Alert,
  Slide,
  SlideProps,
  useMediaQuery,
  LinearProgress,
  Skeleton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'src/store';
import { fetchConversationWithMessages, sendMessage, updateConversationStatus } from 'src/features/account/conversations.slice';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getBranding } from 'src/utils/branding';
import { selectChannelById } from 'src/features/account/channels.slice';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Message from './Message';
import FollowUpIndicator from '../FollowUpIndicator';

interface ConversationPreviewTooltipProps {
  conversationId: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Slide transition for new messages
function SlideTransition(props: Omit<SlideProps, 'direction'>) {
  return <Slide {...props} direction="up" />;
}

// The component with minimal mouse handling logic
const ConversationPreviewTooltip = ({
  conversationId,
  anchorEl,
  onClose,
  open,
  onMouseEnter,
  onMouseLeave
}: ConversationPreviewTooltipProps): React.ReactElement => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Track component mounted state
  const isMountedRef = useRef(true);
  // Track if we've already fetched data
  const hasFetchedRef = useRef(false);
  // Add AbortController ref for cancelling API requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get data from store
  const conversation = useAppSelector(state => state.conversations.entities[conversationId]);
  const channel = useAppSelector(state => selectChannelById(state, conversation?.channel_id || ''));
  const advancedMode = useAppSelector(state => state.user.advancedMode);

  // Compute next follow-up timestamp
  const nextFollowUp = React.useMemo(() => {
    if (conversation?.next_autocomplete_scheduled_at && new Date(conversation.next_autocomplete_scheduled_at) > new Date()) {
      return conversation.next_autocomplete_scheduled_at;
    }
    const pending = conversation?.messages?.filter((m: any) => m.status === 'pending_delivery' && m.deliver_after && new Date(m.deliver_after) > new Date());
    if (pending && pending.length > 0) {
      pending.sort((a: any, b: any) => new Date(a.deliver_after).getTime() - new Date(b.deliver_after).getTime());
      return pending[0].deliver_after;
    }
    return null;
  }, [conversation]);

  // State for message handling
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingError, setSendingError] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<any[]>([]);
  const [entityRole, setEntityRole] = useState<'assistant' | 'function'>('assistant');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Display the slim progress bar whenever we are fetching data or refreshing
  const showLoadingBar = loading || isRefreshing || conversation?.loading;

  // Validate anchorEl
  const isAnchorElValid = useCallback(() => {
    return anchorEl && anchorEl.isConnected && document.body.contains(anchorEl);
  }, [anchorEl]);

  // Get the appropriate sending entity
  const sendingEntity = conversation?.participating_entities?.find(entity => entity.role === entityRole);
  
  // Get the user entity for display in the header
  const userEntity = conversation?.participating_entities?.find(entity => entity.role === 'user');

  // Generate a temporary ID for optimistic updates
  const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  // Combine real messages with optimistic ones and filter based on advanced mode
  const allMessages = React.useMemo(() => {
    const realMessages = conversation?.messages || [];

    const filteredOptimistic = optimisticMessages.filter(opt =>
      !realMessages.some(real =>
        real.sender_role === opt.sender_role &&
        real.content === opt.content &&
        Math.abs(new Date(real.created_at).getTime() - new Date(opt.created_at).getTime()) < 60000
      )
    );

    return [...realMessages, ...filteredOptimistic].filter(msg => advancedMode || msg.sender_role !== 'function');
  }, [conversation, optimisticMessages, advancedMode]);

  // Handle scroll position
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    setIsNearBottom(distanceFromBottom < 100);
  };

  // Scroll to bottom only when needed
  useEffect(() => {
    if (shouldScrollToBottom && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  // Very simple mouse event handlers
  const handleMouseEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Call parent's onMouseEnter to keep the tooltip open
    if (onMouseEnter) onMouseEnter();
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Call parent's onMouseLeave to signal leaving the tooltip
    if (onMouseLeave) onMouseLeave();
  };

  // Comprehensive effect to handle loading state based on data availability
  useEffect(() => {
    // Reset loading when we have either:
    // 1. Conversation with messages
    // 2. Conversation with no messages but confirmed loaded
    // 3. Messages in the allMessages array (including optimistic ones)

    if (
      (conversation && conversation.messages && conversation.messages.length > 0) ||
      (conversation && conversation.id && (!conversation.messages || conversation.messages.length === 0)) ||
      (allMessages && allMessages.length > 0)
    ) {
      setLoading(false);
      setIsRefreshing(false);
      // Scroll to bottom once messages are loaded
      setShouldScrollToBottom(true);
    }
  }, [conversation, allMessages]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // No timeout to clear anymore
    };
  }, []);

  // Load data once when component mounts and is open
  useEffect(() => {
    // Skip if not open or already fetched
    if (!open || !conversationId || hasFetchedRef.current) {
      return;
    }

    // Mark as fetched to prevent duplicate requests
    hasFetchedRef.current = true;

    // Check if we already have some messages to display
    const hasExistingMessages = conversation?.messages?.length > 0;

    // Show loading states appropriately
    setLoading(!hasExistingMessages);
    setIsRefreshing(hasExistingMessages);
    setLoadingError(null);

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    // Store the current AbortController for potential cleanup
    const currentController = abortControllerRef.current;

    dispatch(fetchConversationWithMessages({
      conversationId,
      messagesLimit: 20
    }))
      .then(() => {
        // Force loading to false regardless of mounted state check
        setLoading(false);
        setIsRefreshing(false);

        if (isMountedRef.current && currentController === abortControllerRef.current) {
          // Scroll to bottom after messages are loaded
          setShouldScrollToBottom(true);
        }
      })
      .catch((error) => {
        // Force loading to false regardless of mounted state check
        setLoading(false);
        setIsRefreshing(false);

        if (isMountedRef.current && currentController === abortControllerRef.current && error.name !== 'AbortError') {
          setLoadingError(error.message || 'Failed to load messages');
        }
      });

    // Clean up function to abort fetch if component unmounts or hook re-runs
    return () => {
      if (currentController) {
        currentController.abort();
        if (abortControllerRef.current === currentController) {
          abortControllerRef.current = null;
        }
      }
    };
  }, [conversationId, open, dispatch, conversation, isNearBottom]);

  // Scroll to bottom when the tooltip is opened
  useEffect(() => {
    if (open && messagesContainerRef.current && allMessages.length > 0) {
      setShouldScrollToBottom(true);
    }
  }, [open, allMessages.length]);

  // Reset fetch flag when component unmounts or conversationId changes
  useEffect(() => {
    return () => {
      // Abort any in-flight requests when conversationId changes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      hasFetchedRef.current = false;
      setOptimisticMessages([]);
      setInputValue('');
      setSending(false);
    };
  }, [conversationId]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        if (isMountedRef.current && inputRef.current) {
          inputRef.current.focus();
        }
      }, 200);
    }
  }, [open]);

  // Effect to handle cleanup when the anchor becomes invalid
  useEffect(() => {
    if (!isAnchorElValid() && onClose) {
      onClose();
    }
  }, [anchorEl, isAnchorElValid, onClose]);

  // Simplified message sending function
  const handleSendMessage = () => {
    if (!inputValue.trim() || sending || !sendingEntity) return;

    setSending(true);
    setSendingError(null);

    // Create an optimistic message to show immediately
    const optimisticMessage = {
      id: generateTempId(),
      sender_id: sendingEntity.id,
      content: inputValue,
      created_at: new Date().toISOString(),
      status: 'sending',
      sender_role: entityRole,
      sender_name: sendingEntity.name,
      conversation_id: conversationId,
      isOptimistic: true
    };

    // Add to optimistic messages
    setOptimisticMessages(prev => [...prev, optimisticMessage]);

    // Keep a copy of the message for potential retry
    const messageContent = inputValue;

    // Create AbortController for this request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    // Send the actual message
    dispatch(sendMessage({
      conversation,
      message: messageContent,
      sendingEntity
    }))
      .then(() => {
        if (isMountedRef.current && currentController === abortControllerRef.current) {
          // Remove the optimistic message as it will be replaced by the real one
          setOptimisticMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
          setSending(false);
          // Clear the input only after successful send
          setInputValue('');
          // No need to scroll with flexbox column-reverse
        }
      })
      .catch((error) => {
        if (isMountedRef.current && currentController === abortControllerRef.current && error.name !== 'AbortError') {
          setSending(false);

          // Handle specific error types
          let errorMessage = 'Failed to send message';

          if (error.message === '500 Internal Server Error' ||
            (error.response && error.response.status === 500) ||
            (typeof error === 'object' && error.error === '500 Internal Server Error')) {
            errorMessage = 'Server error occurred. The system may be temporarily unavailable.';
          } else if (error.message) {
            errorMessage = error.message;
          } else if (typeof error === 'string') {
            errorMessage = error;
          } else if (error.error) {
            errorMessage = error.error;
          }

          setSendingError(errorMessage);

          // Update the optimistic message to show error state
          setOptimisticMessages(prev =>
            prev.map(msg =>
              msg.id === optimisticMessage.id
                ? { ...msg, status: 'error', errorDetails: errorMessage }
                : msg
            )
          );
        }
      });
  };

  // Handle pressing Enter to send message
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle toggle entity role
  const handleToggleEntityRole = () => {
    setEntityRole(prev => prev === 'assistant' ? 'function' : 'assistant');
  };

  // Handle refreshing messages
  const handleRefresh = () => {
    if (isRefreshing || loading) return;

    setIsRefreshing(true);
    setLoadingError(null);

    // Abort any previous refresh requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController
    abortControllerRef.current = new AbortController();

    // Store current controller
    const currentController = abortControllerRef.current;

    dispatch(fetchConversationWithMessages({
      conversationId,
      messagesLimit: 20
    }))
      .then(() => {
        // Force loading to false regardless of mounted state check
        setLoading(false);
        setIsRefreshing(false);

        if (isNearBottom) {
          setShouldScrollToBottom(true);
        }
      })
      .catch((error) => {
        // Force loading to false regardless of mounted state check
        setLoading(false);
        setIsRefreshing(false);

        if (isMountedRef.current && currentController === abortControllerRef.current && error.name !== 'AbortError') {
          setLoadingError(error.message || 'Failed to load messages');
        }
      });
  };

  // Handle retry of failed messages
  const handleRetry = (failedMessage: any) => {
    if (sending) return;

    // Update status to 'sending' again
    setOptimisticMessages(prev =>
      prev.map(msg =>
        msg.id === failedMessage.id
          ? { ...msg, status: 'sending' }
          : msg
      )
    );

    // Send again
    const messageData = {
      conversation,
      message: failedMessage.content,
      sendingEntity
    };

    dispatch(sendMessage(messageData))
      .then(() => {
        if (isMountedRef.current) {
          // Remove the optimistic message as it will be replaced by the real one
          setOptimisticMessages(prev => prev.filter(msg => msg.id !== failedMessage.id));
          setShouldScrollToBottom(true);
        }
      })
      .catch((error) => {
        if (isMountedRef.current) {
          // Handle specific error types
          let errorMessage = 'Failed to send message';

          if (error.message === '500 Internal Server Error' ||
            (error.response && error.response.status === 500) ||
            (typeof error === 'object' && error.error === '500 Internal Server Error')) {
            errorMessage = 'Server error occurred. The system may be temporarily unavailable.';
          } else if (error.message) {
            errorMessage = error.message;
          } else if (typeof error === 'string') {
            errorMessage = error;
          } else if (error.error) {
            errorMessage = error.error;
          }

          setSendingError(errorMessage);

          // Update the optimistic message to show error state
          setOptimisticMessages(prev =>
            prev.map(msg =>
              msg.id === failedMessage.id
                ? { ...msg, status: 'error', errorDetails: errorMessage }
                : msg
            )
          );
        }
      });
  };

  // If the anchor element is not valid, don't render the tooltip content
  if (!isAnchorElValid()) {
    return <React.Fragment />;
  }

  return (
    <Popper
      open={open && !isMobile && isAnchorElValid()}
      anchorEl={anchorEl}
      placement="left-start"
      transition
      disablePortal
      sx={{ zIndex: 1300 }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: document.body,
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={300}>
          <Paper
            className="conversation-preview-tooltip"
            elevation={6}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: isMobile ? '90vw' : 450,
              maxWidth: '90vw',
              height: isMobile ? '80vh' : 550,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: `1px solid ${darkMode ? theme.palette.grey[700] : theme.palette.grey[300]}`,
              bgcolor: darkMode ? theme.palette.grey[900] : theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: darkMode
                ? '0 12px 28px 0 rgba(0,0,0,0.7), 0 2px 4px 0 rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1)'
                : '0 12px 28px 0 rgba(0,0,0,0.2), 0 2px 4px 0 rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(10px)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)'
                },
              },
              // Add a distinctive border in development mode
              ...(process.env.NODE_ENV === 'development' && {
                outline: '2px solid #ff5722',
              }),
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
                background: darkMode ? 'black' : theme.palette.background.paper,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'grey.500',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {userEntity?.name?.split(' ').map(n => n[0]).join('') || 'MJ'}
                  </Box>
                  <Box 
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: '#1d9bf0',
                      border: '2px solid black'
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="500"
                    sx={{
                      fontSize: '1.5rem',
                      color: darkMode ? 'white' : theme.palette.text.primary
                    }}
                  >
                    {userEntity?.name || ''}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontSize: '1rem',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {allMessages?.length > 0 && allMessages[allMessages.length - 1]?.sender_role === 'user'
                      ? allMessages[allMessages.length - 1]?.content 
                      : conversation?.subject}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {nextFollowUp && <FollowUpIndicator futureTime={nextFollowUp} size={18} />}
                <Typography 
                  variant="subtitle1" 
                  color="text.secondary"
                  sx={{ fontSize: '0.96rem' }}
                >
                  {dateTimeFormatter(conversation?.latest_delivery_at || conversation?.updated_at || new Date().toISOString(), 'time_only')}
                </Typography>
              </Box>
            </Box>

            {/* Slim top progress indicator */}
            <LinearProgress
              sx={{
                visibility: showLoadingBar ? 'visible' : 'hidden',
                opacity: showLoadingBar ? 1 : 0,
                height: 2,
                transition: 'opacity 0.3s, visibility 0.3s',
              }}
              color="primary"
            />

            {/* Messages Container */}
            <Box
              ref={messagesContainerRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: darkMode
                  ? `linear-gradient(160deg, ${alpha('#000', 0.92)}, ${alpha(theme.palette.grey[900], 0.97)})`
                  : `linear-gradient(160deg, ${alpha('#f5f5f5', 0.8)}, ${alpha(theme.palette.grey[50], 0.95)})`,
                backgroundSize: 'cover',
                backgroundAttachment: 'fixed',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: darkMode ? alpha(theme.palette.grey[600], 0.5) : alpha(theme.palette.grey[400], 0.5),
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: darkMode ? theme.palette.grey[600] : theme.palette.grey[400],
                  }
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                  {[...Array(4)].map((_, idx) => (
                    <Skeleton key={idx} variant="rounded" height={60} animation="wave" />
                  ))}
                </Box>
              ) : loadingError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="error" sx={{ width: '90%' }}>
                    {loadingError}
                  </Alert>
                  <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                    Retry
                  </Button>
                </Box>
              ) : (!conversation || !allMessages || allMessages.length === 0) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
                  <img
                    src={getBranding('oppy_expressions_no_mouth')}
                    alt="No messages"
                    style={{
                      width: 80,
                      height: 80,
                      opacity: 0.5
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" align="center">
                    No messages yet. Start the conversation!
                  </Typography>
                </Box>
              ) : (
                allMessages.map((msg: any) => (
                  <Message
                    key={msg.id}
                    message={msg}
                    sendingEntity={sendingEntity}
                    channelId={channel?.id || conversation?.channel_id}
                    conversationDeliveryState={conversation?.delivery_state}
                    variant="compact"
                    fetchConversationsWithMessagesAction={msg.isOptimistic && msg.status === 'error' ?
                      () => handleRetry(msg) : undefined}
                    isOptimistic={msg.isOptimistic || false}
                    parentConversation={conversation}
                  />
                ))
              )}
            </Box>

            {/* Input Box */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${darkMode ? theme.palette.grey[800] : theme.palette.grey[200]}`,
                bgcolor: darkMode ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                backdropFilter: 'blur(10px)',
                position: 'relative',
              }}
            >
              {/* Status overlay */}
              {(conversation?.status === 'needs_attention' || conversation?.status === 'closed') && (
                <Box
                  onClick={async () => {
                    if (!isUpdatingStatus && conversation) {
                      setIsUpdatingStatus(true);
                      try {
                        await dispatch(updateConversationStatus({
                          conversationId: conversation.id,
                          status: 'standby',
                          channelId: conversation.channel_id
                        })).unwrap();
                      } catch (error) {
                        console.error('Failed to update conversation status:', error);
                      } finally {
                        setIsUpdatingStatus(false);
                      }
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: darkMode 
                      ? alpha(conversation?.status === 'closed' ? '#d32f2f' : '#ed6c02', 0.15)
                      : alpha(conversation?.status === 'closed' ? '#d32f2f' : '#ed6c02', 0.08),
                    backdropFilter: 'blur(2px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isUpdatingStatus ? 'wait' : 'pointer',
                    zIndex: 10,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: darkMode 
                        ? alpha(conversation?.status === 'closed' ? '#d32f2f' : '#ed6c02', 0.25)
                        : alpha(conversation?.status === 'closed' ? '#d32f2f' : '#ed6c02', 0.15),
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    {isUpdatingStatus ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            color: conversation?.status === 'closed' ? '#d32f2f' : '#ed6c02',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          {conversation?.status === 'closed' ? 'Conversation Closed' : 'Needs Attention'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                            fontSize: '0.75rem',
                          }}
                        >
                          Click to reopen
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              )}

              <Tooltip
                title={entityRole === 'assistant'
                  ? `Currently sending as ${channel?.chatbot_name || 'Assistant'}. Click to switch to Function mode.`
                  : "Currently sending as Function. Click to switch to Assistant mode."}
                placement="top"
              >
                <IconButton
                  onClick={handleToggleEntityRole}
                  color={entityRole === 'assistant' ? 'primary' : 'secondary'}
                  sx={{
                    bgcolor: alpha(
                      entityRole === 'assistant'
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                      darkMode ? 0.15 : 0.1
                    ),
                    '&:hover': {
                      bgcolor: alpha(
                        entityRole === 'assistant'
                          ? theme.palette.primary.main
                          : theme.palette.secondary.main,
                        darkMode ? 0.25 : 0.2
                      ),
                    }
                  }}
                >
                  {entityRole === 'assistant' ? (
                    <img
                      src={getBranding('oppy_expressions_no_mouth')}
                      alt="Assistant Icon"
                      style={{ width: '20px', height: '20px' }}
                    />
                  ) : (
                    <TipsAndUpdatesIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <TextField
                fullWidth
                size="small"
                placeholder={entityRole === 'assistant'
                  ? `Message as ${channel?.chatbot_name || 'Assistant'}...`
                  : "Internal prompt..."}
                multiline
                maxRows={4}
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  if (sendingError) setSendingError(null);
                }}
                onKeyDown={handleKeyDown}
                inputRef={inputRef}
                disabled={sending || loading}
                error={!!sendingError}
                helperText={sendingError || undefined}
                InputProps={{
                  sx: {
                    bgcolor: darkMode ? alpha(theme.palette.grey[800], 0.8) : alpha(theme.palette.grey[50], 0.8),
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: entityRole === 'assistant' ? theme.palette.primary.main : theme.palette.secondary.main,
                      borderWidth: 2,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: entityRole === 'assistant'
                        ? alpha(theme.palette.primary.main, 0.7)
                        : alpha(theme.palette.secondary.main, 0.7),
                    },
                    transition: 'all 0.2s ease',
                  }
                }}
              />

              <IconButton
                color={entityRole === 'assistant' ? 'primary' : 'secondary'}
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || sending || loading}
                sx={{
                  bgcolor: entityRole === 'assistant' ? theme.palette.primary.main : theme.palette.secondary.main,
                  color: entityRole === 'assistant'
                    ? theme.palette.primary.contrastText
                    : theme.palette.secondary.contrastText,
                  '&:hover': {
                    bgcolor: entityRole === 'assistant'
                      ? theme.palette.primary.dark
                      : theme.palette.secondary.dark,
                  },
                  transition: 'all 0.2s ease',
                  '&.Mui-disabled': {
                    bgcolor: darkMode ? theme.palette.grey[800] : theme.palette.grey[300],
                    color: darkMode ? theme.palette.grey[600] : theme.palette.grey[500],
                  },
                  height: 40,
                  width: 40,
                }}
              >
                {sending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>

            {/* Error snackbar */}
            <Snackbar
              open={!!sendingError}
              autoHideDuration={5000}
              onClose={() => setSendingError(null)}
              TransitionComponent={SlideTransition}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert onClose={() => setSendingError(null)} severity="error" sx={{ width: '100%' }}>
                {sendingError}
              </Alert>
            </Snackbar>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

export default ConversationPreviewTooltip; 
